"use client";

import type { OrganizationPermissions } from "@/lib/orgs";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import type { UseGhostCursorReturn } from "realtime-voice-component";
import { z } from "zod";
import type { VoiceRiskLevel } from "./voiceCapabilities";

export const MAX_TOOL_ITEMS = 20;
export const DEFAULT_TOOL_ITEMS = 8;

export const DASHBOARD_PROMPT_TARGET = "dashboard-prompt-input";
export const DASHBOARD_SUBMIT_TARGET = "dashboard-submit-prompt";
export const DASHBOARD_CADDIE_TARGET = "dashboard-caddie-button";

export const CONFIRMABLE_RISKS = new Set<VoiceRiskLevel>(["write", "destructive", "financial"]);

export type VoiceNoticeTone = "error" | "info" | "success";
export type VoiceTemplateCategory = "promoted" | "public" | "org" | "all";

export type VoicePendingConfirmation = {
  action: string;
  createdAt: number;
  description: string;
  details?: unknown;
  risk: VoiceRiskLevel;
  title: string;
  token: string;
};

export type VoicePendingAction = VoicePendingConfirmation & {
  execute: () => Promise<unknown> | unknown;
  successMessage?: string;
};

export type ConfirmationRequest = Omit<VoicePendingAction, "createdAt" | "token">;

export type B3OSVoiceToolsOptions = {
  cancelPendingAction: (token?: string) => VoicePendingConfirmation | null;
  confirmPendingAction: (token?: string) => Promise<unknown>;
  currentOrgPermissions?: OrganizationPermissions | null;
  currentOrgRole?: "owner" | "developer" | null;
  getActionHistory: () => VoicePendingConfirmation[];
  getPendingConfirmation: () => VoicePendingConfirmation | null;
  pathname: string;
  pushNotice: (tone: VoiceNoticeTone, title: string, detail?: string) => void;
  requestConfirmation: (request: ConfirmationRequest) => VoicePendingConfirmation;
  router: AppRouterInstance;
  run: UseGhostCursorReturn["run"];
  selectedOrgId: string | null | undefined;
  selectedOrgName?: string | null;
};

export type Resolution<T> =
  | { ok: true; item: T }
  | {
      ok: false;
      matches?: unknown[];
      reason: string;
    };

export const workflowStatusSchema = z.enum(["active", "draft", "paused", "archived"]);
export const workflowVisibilitySchema = z.enum(["private", "org", "public", "public_view", "public_execute"]);
export const workflowViewSchema = z.enum(["builder", "summary", "json", "metrics", "history", "versions"]);
export const workflowRunStatusSchema = z.enum([
  "success",
  "partial",
  "failure",
  "pending",
  "running",
  "cancelled",
  "waiting",
]);
export const orderStatusSchema = z.enum(["active", "triggered", "executing", "filled", "cancelled", "failed"]);
export const orderTypeSchema = z.enum(["buy", "sell"]);
export const templateCategorySchema = z.enum(["promoted", "public", "org", "all"]);
export const organizationRoleSchema = z.enum(["owner", "developer"]);
